/**
 * 기술지원확약서 작성/수정 모달.
 *
 * 입력 항목 7개:
 *   - 문서번호 (YYYYMMDD-NN, 자동 채움 + 수정 가능)
 *   - 발급일자 (오늘 기본, 변경 시 문서번호 자동 재계산)
 *   - 수요기관 / 사업명 / 공급사 / 참조 / 제품명 (자유 입력)
 */

import { todayKst } from '../../lib/date-label';
import { useEffect, useState } from 'react';
import {
  fetchTechSupportNextNo,
  type TechSupportDetail,
  type TechSupportInput,
} from '../../lib/tech-support-api';

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initial: TechSupportDetail | null;
  onClose: () => void;
  onSubmit: (data: TechSupportInput) => Promise<void>;
}

function todayStr(): string {
  return todayKst();
}

const EMPTY: TechSupportInput = {
  docNo: '',
  issueDate: todayStr(),
  recipient: '',
  businessName: '',
  supplier: '',
  ccRef: '',
  productName: '',
};

export function TechSupportFormModal({ open, mode, initial, onClose, onSubmit }: Props) {
  const [form, setForm] = useState<TechSupportInput>(EMPTY);
  const [docNoTouched, setDocNoTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // 모달 열릴 때 초기값 세팅
  useEffect(() => {
    if (!open) return;
    setErr(null);
    setSubmitting(false);
    if (mode === 'edit' && initial) {
      setForm({
        docNo: initial.docNo,
        issueDate: initial.issueDate,
        recipient: initial.recipient,
        businessName: initial.businessName,
        supplier: initial.supplier,
        ccRef: initial.ccRef,
        productName: initial.productName,
      });
      setDocNoTouched(true); // 수정 모드에선 자동 갱신 금지
    } else {
      setForm({ ...EMPTY, issueDate: todayStr() });
      setDocNoTouched(false);
      // 자동 채번
      fetchTechSupportNextNo(todayStr())
        .then((r) => setForm((f) => ({ ...f, docNo: r.docNo, issueDate: r.issueDate })))
        .catch(() => {/* 빈 채로 둠 */});
    }
  }, [open, mode, initial]);

  // 발급일자 변경 → 문서번호 자동 재계산 (작성 모드 + docNo 미수정 시)
  useEffect(() => {
    if (!open || mode !== 'create' || docNoTouched) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.issueDate)) return;
    fetchTechSupportNextNo(form.issueDate)
      .then((r) => setForm((f) => ({ ...f, docNo: r.docNo })))
      .catch(() => {/* 무시 */});
  }, [open, mode, docNoTouched, form.issueDate]);

  if (!open) return null;

  async function handleSubmit() {
    setErr(null);
    if (!form.docNo.trim()) { setErr('문서번호를 입력하세요.'); return; }
    if (!/^\d{8}-\d{2,}$/.test(form.docNo.trim())) {
      setErr('문서번호 형식: YYYYMMDD-NN (예: 20260520-01)');
      return;
    }
    if (!form.issueDate) { setErr('발급일자를 입력하세요.'); return; }
    if (!form.recipient.trim()) { setErr('수요기관을 입력하세요.'); return; }
    setSubmitting(true);
    try {
      await onSubmit({
        docNo: form.docNo.trim(),
        issueDate: form.issueDate,
        recipient: form.recipient.trim(),
        businessName: form.businessName.trim(),
        supplier: form.supplier.trim(),
        ccRef: form.ccRef.trim(),
        productName: form.productName.trim(),
      });
    } catch (e) {
      setErr((e as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        className="bg-white rounded shadow-xl w-[600px] max-w-[95vw] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="ui-section-title">
            기술지원확약서 {mode === 'create' ? '작성' : '수정'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="ui-close"
            title="닫기"
          >
            ✕
          </button>
        </div>

        {/* 본문 */}
        <div className="px-5 py-4 space-y-3">
          <Field label="문서번호" required>
            <input
              type="text"
              value={form.docNo}
              onChange={(e) => { setDocNoTouched(true); setForm({ ...form, docNo: e.target.value }); }}
              placeholder="20260520-01"
              className="ui-input w-full font-mono"
            />
            <div className="text-xs text-slate-500 mt-0.5">
              형식: YYYYMMDD-NN — 발급일자에 따라 자동 채움, 수정 가능
            </div>
          </Field>

          <Field label="발급일자" required>
            <input
              type="date"
              value={form.issueDate}
              onChange={(e) => setForm({ ...form, issueDate: e.target.value })}
              className="ui-input"
            />
          </Field>

          <Field label="수요기관" required>
            <input
              type="text"
              value={form.recipient}
              onChange={(e) => setForm({ ...form, recipient: e.target.value })}
              placeholder="예: ○○청"
              className="ui-input w-full"
            />
          </Field>

          <Field label="사업명">
            <input
              type="text"
              value={form.businessName}
              onChange={(e) => setForm({ ...form, businessName: e.target.value })}
              placeholder="예: ○○ 시스템 구축 사업"
              className="ui-input w-full"
            />
          </Field>

          <Field label="공급사">
            <input
              type="text"
              value={form.supplier}
              onChange={(e) => setForm({ ...form, supplier: e.target.value })}
              placeholder="예: (주)에스엠시스템즈"
              className="ui-input w-full"
            />
          </Field>

          <Field label="참조">
            <input
              type="text"
              value={form.ccRef}
              onChange={(e) => setForm({ ...form, ccRef: e.target.value })}
              placeholder="예: ○○○ 담당자님"
              className="ui-input w-full"
            />
          </Field>

          <Field label="제품명">
            <textarea
              value={form.productName}
              onChange={(e) => setForm({ ...form, productName: e.target.value })}
              placeholder="여러 줄 입력 가능"
              rows={3}
              className="ui-input ui-textarea w-full resize-y"
            />
          </Field>

          {err && (
            <div className="px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-sm">
              {err}
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="btn btn-secondary"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="btn btn-primary"
          >
            {submitting ? '저장 중...' : mode === 'create' ? '작성' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="ui-label block mb-1">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
